import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BookOpen, ChatCircleDots, LockSimple, PaperPlaneTilt } from '@phosphor-icons/react';
import { BASE_URL } from '../../api/client';
import { useAuth } from '../../store/AuthContext';
import { useUI } from '../../store/UIContext';
import { Btn, PageHeader, Spinner } from '../../components/ui/index';
import { EmojiBar } from '../../components/ui/EmojiPicker';
import { isCustomerRole } from '../../utils/customerTicketStatus';
import { sanitizeHelpHtml } from '../../utils/sanitizeHelpHtml';

const SUGGESTIONS = [
  { q: 'می‌خواهم تیکت بزنم؛ چطور؟', path: '/tickets/new' },
  { q: 'چطور اطلاعاتم را ویرایش کنم؟', path: '/profile/edit' },
  { q: 'نرم‌افزار چه امکاناتی دارد؟', path: null },
];

function isRegistered(user) {
  if (!isCustomerRole(user?.type)) return false;
  return !!(user.phone_registred || user.email_registred);
}

function isAppPath(value) {
  if (!value || typeof value !== 'string') return false;
  return value.startsWith('/') && !value.startsWith('//');
}

function HelpHtml({ html, onAppPath }) {
  const onClick = (e) => {
    const a = e.target.closest('a');
    if (!a) return;
    const href = a.getAttribute('href') || '';
    if (!isAppPath(href)) return;
    e.preventDefault();
    onAppPath(href);
  };
  return (
    <div
      className="pj-help-html"
      onClick={onClick}
      dangerouslySetInnerHTML={{ __html: sanitizeHelpHtml(html) }}
    />
  );
}

export default function SmartHelpPage() {
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const { addToast } = useUI();
  const registered = isRegistered(user);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [chatId, setChatId] = useState(null);
  const [related, setRelated] = useState([]);
  const [helperFileUrl, setHelperFileUrl] = useState(null);
  const esRef = useRef(null);
  const endRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => () => esRef.current?.close(), []);
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, related, loading]);

  const go = (path) => {
    if (isAppPath(path)) navigate(path);
  };

  const patchLast = (fields) => {
    setMessages((prev) => {
      const next = [...prev];
      next[next.length - 1] = { ...next[next.length - 1], ...fields };
      return next;
    });
  };

  const send = (text, historyOverride) => {
    const question = (text || '').trim();
    if (!question || loading || !registered) return;

    const history = [...(historyOverride || messages), { role: 'user', content: question }];
    setMessages([...history, { role: 'assistant', content: '', related: [], actions: [], helperUrl: null }]);
    setInput('');
    setLoading(true);
    setRelated([]);
    setHelperFileUrl(null);

    // POST (not EventSource/GET) so a long chat history rides in the body
    // instead of the URL - a long history pushed the GET query string past
    // the proxy's URL-length limit.
    const controller = new AbortController();
    esRef.current = { close: () => controller.abort() };
    let assistantHtml = '';
    let done = false;

    const handleEvent = (raw) => {
      if (raw === '[DONE]') {
        done = true;
        setLoading(false);
        return;
      }
      try {
        const event = JSON.parse(raw);
        if (event.type === 'chat_id') {
          setChatId(event.chat_id);
        } else if (event.type === 'chunk') {
          assistantHtml += event.content || '';
          patchLast({ role: 'assistant', content: assistantHtml });
        } else if (event.type === 'related') {
          const items = Array.isArray(event.items) ? event.items : [];
          setRelated(items);
          patchLast({ related: items });
        } else if (event.type === 'actions') {
          const items = Array.isArray(event.items) ? event.items : [];
          patchLast({ actions: items });
        } else if (event.type === 'navigate' && isAppPath(event.path)) {
          go(event.path);
        } else if (event.type === 'file') {
          const href = event.helper_bat_file_address || null;
          setHelperFileUrl(href);
          patchLast({ helperUrl: href });
        } else if (event.type === 'error') {
          addToast('خطا در دریافت پاسخ راهنما', 'error');
        }
      } catch {}
    };

    fetch(`${BASE_URL}ask_smart_help`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ history, chat_id: chatId || undefined }),
      signal: controller.signal,
    })
      .then(async (res) => {
        if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        for (;;) {
          const { value, done: streamDone } = await reader.read();
          if (streamDone) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n\n');
          buffer = lines.pop();
          for (const line of lines) {
            if (line.startsWith('data: ')) handleEvent(line.slice(6).trim());
          }
        }
        if (!done) setLoading(false);
      })
      .catch(() => {
        if (done) return;
        addToast('ارتباط با راهنمای هوشمند قطع شد', 'error');
        setLoading(false);
      });
  };

  const onSuggestion = (item) => {
    if (item.path) {
      go(item.path);
      return;
    }
    send(item.q, []);
  };

  const onRelated = (item) => {
    if (isAppPath(item.path)) {
      go(item.path);
      return;
    }
    send(item.title);
  };

  if (!registered) {
    return (
      <div className="fade-in">
        <PageHeader title="راهنمای هوشمند" subtitle="راهنمای کار با سامانه CRM پژواک" />
        <div className="pj-help-lock">
          <div className="pj-help-lock__icon" aria-hidden="true">
            <LockSimple size={28} weight="fill" />
          </div>
          <h2>این بخش فقط برای کاربران ثبت‌شده است</h2>
          <p>برای پرسش از راهنمای هوشمند، ابتدا شماره موبایل یا ایمیل خود را در پروفایل تأیید کنید.</p>
          <Link to="/profile/edit">
            <Btn>تکمیل ثبت‌نام</Btn>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="fade-in pj-help-page">
      <PageHeader title="راهنمای هوشمند" subtitle="ثبت تیکت، ویرایش اطلاعات و امکانات سامانه را بپرسید" />

      <div className="pj-help-shell">
        <div className="pj-help-thread" role="log" aria-live="polite">
          {messages.length === 0 && (
            <div className="pj-help-empty">
              <div className="pj-help-empty__icon" aria-hidden="true">
                <ChatCircleDots size={32} weight="fill" />
              </div>
              <p>یکی از سوال‌ها را انتخاب کنید تا مستقیم به همان صفحه بروید، یا سوال خود را بنویسید.</p>
              <div className="pj-help-suggestions">
                {SUGGESTIONS.map((item) => (
                  <button key={item.q} type="button" className="pj-help-chip" onClick={() => onSuggestion(item)}>
                    {item.q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <div key={i} className={`pj-help-row pj-help-row--${m.role}`}>
              <div className={`pj-help-bubble pj-help-bubble--${m.role}`}>
                {m.role === 'user' ? (
                  <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{m.content}</p>
                ) : (
                  <>
                    {m.content ? (
                      <HelpHtml html={m.content} onAppPath={go} />
                    ) : (
                      <span className="pj-help-typing">
                        <Spinner size={14} /> در حال نوشتن پاسخ…
                      </span>
                    )}
                    {m.actions?.length > 0 && (
                      <div className="pj-help-suggestions" style={{ marginTop: 10 }}>
                        {m.actions.map((item) => (
                          <button
                            key={item.path || item.label}
                            type="button"
                            className="pj-help-chip"
                            onClick={() => go(item.path)}
                          >
                            {item.label}
                          </button>
                        ))}
                      </div>
                    )}
                    {m.related?.length > 0 && (
                      <div className="pj-help-related">
                        <div className="pj-help-related__label">
                          <BookOpen size={14} weight="bold" /> مطالب مرتبط
                        </div>
                        {m.related.map((item) => (
                          <button
                            key={item.id || item.title}
                            type="button"
                            className="pj-help-chip"
                            onClick={() => onRelated(item)}
                          >
                            {item.title}
                          </button>
                        ))}
                      </div>
                    )}
                    {m.helperUrl && (
                      <a className="pj-help-file" href={m.helperUrl} download>
                        دانلود فایل راهنما
                      </a>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}
          <div ref={endRef} />
        </div>

        <form
          className="pj-help-composer"
          onSubmit={(e) => {
            e.preventDefault();
            send(input);
          }}
        >
          <EmojiBar inputRef={inputRef} value={input} onInsert={setInput} />
          <input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="سوال خود را بنویسید..."
            disabled={loading}
            aria-label="متن سوال"
          />
          <Btn type="submit" disabled={loading || !input.trim()} style={{ minHeight: 44 }}>
            <PaperPlaneTilt size={16} weight="bold" />
            ارسال
          </Btn>
        </form>
      </div>
    </div>
  );
}
